<script setup lang="ts">
import {waitingListSchema, type WaitingListForm} from "~/schemas/WaitingList";
import MarqueeAlpha from "~/components/MarqueeAlpha.vue";
import HeroNotHome from "~/components/HeroNotHome.vue";

useWreadiaSeo({
  title: "Rejoins le club de la fiction indépendante",
});

const state = reactive({
  last_name: "",
  first_name: "",
  email: "",
  birth_date: "",
  optin_tos: false as any,
  optin_data_processing: false as any,
  token: "",
  type: "reader",
});

const turnstile = ref();
const loading = ref(false);
const submitted = ref(false);
const error = ref<string | null>(null);

async function onSubmit(event: any) {
  loading.value = true;
  error.value = null;
  try {
    const data = {
      ...state,
    };
    await $api("/api/waiting-list", {
      method: "POST",
      body: data,
    });
    submitted.value = true;
  } catch (err: any) {
    error.value = err.data?.message || "Une erreur est survenue lors de l'envoi de votre candidature.";
    turnstile.value?.reset();
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <UPage id="pagePage">
    <div id="pageBody" class="mt-0 bg-gray-100">

      <section class="m-0 border-b-2 border-primary bg-gradient-nugget md:pb-0">
        <MarqueeAlpha></MarqueeAlpha>
        <HeroNotHome line1="Rejoins" line2="le club."></HeroNotHome>
      </section>

      <section class="m-0 py-8 md:py-16">
        <UContainer>
          <div class="text-center">
            <div class="mb-16">
              <h2 class="">On a hâte de t’accueillir !</h2>
              <p class="text-lg">Partage-nous tes infos, on crée ton profil.</p>
            </div>
            <UCard class="max-w-2xl mx-auto text-left" color="neutral" variant="outline">
              <div v-if="submitted" class="py-8 text-center">
                <UIcon name="i-custom-party" class="size-16 text-success mx-auto mb-4"/>
                <h3 class="text-2xl font-bold mb-2">Bienvenue dans le club, {{ state.first_name }} !</h3>
                <p class="text-gray-600">On t’envoie un email très vite pour te connecter.</p>
              </div>

              <UForm v-else :schema="waitingListSchema" :state="state" class="space-y-6" @submit="onSubmit">
                <UAlert color="secondary" variant="solid" title="Places limitées pendant l'alpha !"></UAlert>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <UFormField label="Nom" name="last_name" required>
                    <UInput v-model="state.last_name" placeholder="Votre nom" class="w-full"/>
                  </UFormField>
                  <UFormField label="Prénom" name="first_name" required>
                    <UInput v-model="state.first_name" placeholder="Votre prénom" class="w-full"/>
                  </UFormField>
                </div>

                <UFormField label="Email" name="email" required>
                  <UInput v-model="state.email" placeholder="votre@email.com" class="w-full"/>
                </UFormField>

                <UFormField label="Date de naissance" name="birth_date" required description="Certaines histoires sont réservées aux 18 ans et plus.">
                  <UInput v-model="state.birth_date" type="date" class="w-full"/>
                </UFormField>

                <div class="space-y-4 pt-4 border-t border-gray-200">
                  <UFormField name="optin_tos" required>
                    <UCheckbox v-model="state.optin_tos">
                      <template #label>
                        J’ai lu et j’accepte les <ULink href="/cgu" target="_blank">conditions générales d’utilisation</ULink>.
                      </template>
                    </UCheckbox>
                  </UFormField>

                  <UFormField name="optin_data_processing" required>
                    <UCheckbox
                        v-model="state.optin_data_processing">
                      <template #label>
                        En soumettant ce formulaire, j’accepte que mes informations soient traitées par Wreadia SASU pour l’examen de ma candidature.
                        Je dispose d’un droit d’accès, de rectification, d’effacement, d’opposition et de portabilité de mes données.
                        <ULink href="/confidentialite" target="_blank">Politique de confidentialité</ULink>
                        .
                      </template>
                    </UCheckbox>
                  </UFormField>
                </div>

                <NuxtTurnstile ref="turnstile" v-model="state.token"></NuxtTurnstile>

                <UAlert v-if="error" color="error" variant="solid" :title="error" icon="i-heroicons-exclamation-triangle"/>

                <UButton type="submit" color="primary" variant="solid" block :loading="loading">
                  Rejoindre le club
                </UButton>
              </UForm>
            </UCard>
          </div>
        </UContainer>
      </section>

      <USeparator/>

      <section class="m-0 py-8 md:py-16">
        <UContainer>
          <div class="text-center mb-12">
            <h2 class="mb-16">Ce que tu trouveras au club</h2>
            <div class="flex flex-col gap-8">
              <div class="lg:max-w-1/2 m-auto">
                <h3 class="text-2xl font-medium mb-2">Plonge dans de nouveaux mondes et histoires.</h3>
                <p class="text-lg">Romance, Fantasy, New Adult..tes genres préférés et tous les tropes seront ici!<br>
                  À chaque histoire, un univers à explorer et une voix à découvrir.</p>
              </div>
              <div class="lg:max-w-1/2 m-auto">
                <h3 class="text-2xl font-medium mb-2">Une communauté qui lit avec toi.</h3>
                <p class="text-lg">Tu peux partager tes émotions au fil des chapitres. Lire devient un moment qui se vit ensemble.</p>
              </div>
              <div class="lg:max-w-1/2 m-auto">
                <h3 class="text-2xl font-medium mb-2">Un lien direct avec les autrices et auteurs.</h3>
                <p class="text-lg">Sur Wreadia, tu fais vivre les histoires que tu aimes.</p>
              </div>
              <div class="lg:max-w-1/2 m-auto">
                <h3 class="text-2xl font-medium mb-2">Pendant l’alpha, c’est gratuit et sans engagement.</h3>
                <p class="text-lg">Le nombre de places est limité.
                </p>
              </div>
            </div>
          </div>
          <p class="text-center text-4xl font-serif">Bienvenue à Wreadia,<br>le club de la fiction indé.</p>
        </UContainer>
      </section>
    </div>
  </UPage>
</template>

<style scoped>

</style>
